import { useMemo, useState } from "react";
import {
  ShieldCheck,
  AlertTriangle,
  ShieldX,
  HelpCircle,
  Stethoscope,
  Flag,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { submitResultReport } from "../lib/api";
import { useAuth } from "../lib/auth";
import { cn } from "../lib/utils";
import SafeSwapSection from "./SafeSwapSection";
import DiscussionReportCard from "./DiscussionReportCard";
import ShareControls from "./ShareControls";
import ResultOgMeta from "./ResultOgMeta";
import SaveCheckBox from "./SaveCheckBox";
import { VerifiedFinding, UncoveredActivesNote, InsufficientActivesPanel } from "./VerifiedFindings";

// Four honest verdict states. Every verdict is produced ONLY from verified,
// sourced interaction rows — there is no score, no confidence badge, and no
// AI-inferred anything anywhere on this card.
const VERDICT = {
  AVOID: {
    word: "AVOID",
    reason: "This combination has a verified contraindication or high-irritation risk.",
    icon: ShieldX,
    bg: "bg-avoid-bg", border: "border-avoid-border",
    text: "text-avoid-deep", accent: "bg-avoid-solid", soft: "text-avoid-text",
  },
  USE_WITH_CAUTION: {
    word: "USE WITH CAUTION",
    reason: "There's a verified caution for this combination.",
    icon: AlertTriangle,
    bg: "bg-caution-bg", border: "border-caution-border",
    text: "text-caution-deep", accent: "bg-caution-solid", soft: "text-caution-text",
  },
  SAFE_TOGETHER: {
    word: "No label conflict found",
    reason: "Every active in this product has a verified, sourced note saying it's compatible.",
    icon: ShieldCheck,
    bg: "bg-safe-bg", border: "border-safe-border",
    text: "text-safe-deep", accent: "bg-safe-solid", soft: "text-safe-text",
  },
  INSUFFICIENT_DATA: {
    word: "NOT ENOUGH VERIFIED DATA",
    reason: "",
    icon: HelpCircle,
    bg: "bg-cream-100", border: "border-surface-line",
    text: "text-ink", accent: "bg-ink", soft: "text-ink-soft",
  },
};

function medDisplay(m) {
  if (!m) return "";
  const base = m.generic_name || m.display_name || m.name || "";
  const brands = Array.isArray(m.brand_names) ? m.brand_names : [];
  const brand = brands.find((b) => b && b.toLowerCase() !== (base || "").toLowerCase());
  return brand ? `${base} (${brand})` : base;
}

// One verdict banner for a single medication × product pair. With multiple
// medications the card renders one of these per medication, each with its own
// verdict — there is no combined "MedA + MedB" headline.
function VerdictBanner({
  verdict,
  verdictDisplay,
  medLabel,
  productName,
  productNotFound,
  findings = [],
  uncovered = [],
  result,
  onPasteFallback,
  onTryAnotherProduct,
  showInsufficientPanel = false,
}) {
  const tone = VERDICT[verdict] || VERDICT.INSUFFICIENT_DATA;
  const Icon = tone.icon;
  const isInsufficient = verdict === "INSUFFICIENT_DATA";
  const reason = !isInsufficient ? (findings[0]?.explanation || tone.reason) : "";
  return (
    <div
      data-testid="result-banner"
      data-verdict={verdict}
      className={cn("rounded-3xl border overflow-hidden transition-all duration-300", tone.bg, tone.border)}
    >
      <div className="p-7 sm:p-8">
        <div className="flex items-start gap-5">
          <span className={cn("inline-flex items-center justify-center w-14 h-14 rounded-2xl text-white shrink-0", tone.accent)}>
            <Icon className="w-7 h-7" strokeWidth={2} />
          </span>
          <div className="flex-1 min-w-0">
            <div
              data-testid="result-verdict-word"
              className={cn("text-xs uppercase tracking-[0.22em] font-bold", tone.text)}
            >
              {productNotFound ? "PRODUCT NOT FOUND" : (verdictDisplay || tone.word)}
            </div>
            <h2
              data-testid="result-headline"
              className={cn("text-2xl sm:text-3xl mt-2 tracking-[-0.02em] leading-[1.1] font-extrabold", tone.text)}
            >
              {medLabel}
              <span className="opacity-50 font-normal mx-2">×</span>
              <span className="font-bold">{productName}</span>
            </h2>
            {reason && (
              <p data-testid="result-reason" className={cn("mt-3 text-[15px] leading-relaxed", tone.soft)}>
                {reason}
              </p>
            )}
          </div>
        </div>

        {isInsufficient ? (
          showInsufficientPanel ? (
            <InsufficientActivesPanel
              result={result}
              onPasteFallback={onPasteFallback}
              onTryAnotherProduct={onTryAnotherProduct}
            />
          ) : (
            <p className="mt-5 text-[13px] leading-relaxed text-ink-soft">
              No verified label conflict is on file for this pairing yet.
            </p>
          )
        ) : (
          <div className="mt-6 space-y-3">
            {findings.map((f, i) => (
              <VerifiedFinding key={`${f.ingredient_id}-${f.medication_id}-${i}`} f={f} />
            ))}
            <UncoveredActivesNote uncovered={uncovered} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function ResultCard({ result, onRecheck, onPaywall, onPasteFallback, onTryAnotherProduct }) {
  const overallVerdict = result.verdict || "INSUFFICIENT_DATA";
  const productNotFound = !!result.product_not_found;
  const productName = result.product_name;

  const allFindings = result.verified_findings || [];
  const uncovered = result.uncovered_actives || [];
  const isInsufficient = overallVerdict === "INSUFFICIENT_DATA";
  const isSafe = overallVerdict === "SAFE_TOGETHER";
  const isRisky = overallVerdict === "AVOID" || overallVerdict === "USE_WITH_CAUTION";

  const meds = Array.isArray(result.medications) ? result.medications : [];
  const isMulti = meds.length > 1;

  // Flag-form label: generic names (brand in brackets), joined.
  const medLabel = useMemo(() => {
    if (meds.length) return meds.map(medDisplay).join(" + ");
    return medDisplay(result.medication) || result.medication?.name || "";
  }, [meds, result.medication]);

  return (
    <div data-testid="result-card" className="space-y-6 animate-fade-up">
      <ResultOgMeta historyId={result.history_id || result.check_history_id || result.id} />

      {/* ====== Verdict banner(s) — one per medication × product pair ====== */}
      {isMulti ? (
        <div className="space-y-4" data-testid="result-banner-group">
          {meds.map((m) => (
            <VerdictBanner
              key={m.id}
              verdict={m.verdict || overallVerdict}
              verdictDisplay={m.verdict_display}
              medLabel={medDisplay(m)}
              productName={productName}
              productNotFound={productNotFound}
              findings={Array.isArray(m.findings) ? m.findings : allFindings.filter((f) => f.medication_id === m.id)}
            />
          ))}
          {isInsufficient && (
            <InsufficientActivesPanel
              result={result}
              onPasteFallback={onPasteFallback}
              onTryAnotherProduct={onTryAnotherProduct}
              messageOverride={allFindings.length > 0
                ? "Some of your medications aren't reviewed for this product yet — see which below. The verdicts above cover only the medications shown with a result."
                : undefined}
            />
          )}
        </div>
      ) : (
        <VerdictBanner
          verdict={overallVerdict}
          verdictDisplay={result.verdict_display}
          medLabel={medDisplay(result.medication || meds[0])}
          productName={productName}
          productNotFound={productNotFound}
          findings={allFindings}
          uncovered={uncovered}
          result={result}
          onPasteFallback={onPasteFallback}
          onTryAnotherProduct={onTryAnotherProduct}
          showInsufficientPanel
        />
      )}

      <p data-testid="result-disclaimer" className="text-[12px] leading-relaxed text-ink-mute">
        Information from the FDA label and published sources. Not medical advice.
      </p>

      {/* ============ What to use instead (AVOID / CAUTION) ============ */}
      {isRisky && <SafeSwapSection result={result} onRecheck={onRecheck} onPaywall={onPaywall} />}

      {/* ============ When to call your doctor (clinician watch_for) ====== */}
      {result.medication?.watch_for ? (
        <div
          data-testid="when-to-call-doctor"
          className="rounded-2xl border border-surface-line bg-white px-5 py-4 flex items-start gap-3"
        >
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-cream-100 border border-surface-line text-ink-soft shrink-0">
            <Stethoscope className="w-4 h-4" />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink-mute">When to call your doctor</div>
            <p data-testid="watch-for-text" className="text-[13.5px] text-ink-soft leading-relaxed mt-1">
              {result.medication.watch_for}
            </p>
            {(() => {
              const s = (result.medication.watch_for_sources || [])[0];
              const url = typeof s === "string" ? s : s?.url;
              const label = typeof s === "string" ? "Source" : (s?.label || s?.name || "Source");
              return url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="watch-for-source"
                  className="mt-1.5 inline-block text-[12px] font-semibold text-ink underline underline-offset-2 hover:opacity-80"
                >
                  {label} ↗
                </a>
              ) : null;
            })()}
          </div>
        </div>
      ) : null}

      {/* ============ Save this check (verified verdicts) ============ */}
      {!isInsufficient && <SaveCheckBox result={result} />}

      {/* ============ Share — only on a verified SAFE_TOGETHER ============ */}
      {isSafe && (
        <div className="rounded-2xl border border-surface-line bg-white px-5 py-4">
          <ShareControls result={result} />
        </div>
      )}

      {/* ============ Doctor Discussion Report (Pro) — risky verdicts ====== */}
      {isRisky && <DiscussionReportCard result={result} onPaywall={onPaywall} />}

      {/* ============ Report this result ============ */}
      <ReportResult result={result} medLabel={medLabel} />
    </div>
  );
}


// --------------------------------------------------------------------------- //
function ReportResult({ result, medLabel }) {
  const [open, setOpen] = useState(false);
  const [issue, setIssue] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!issue.trim()) return;
    setBusy(true);
    try {
      await submitResultReport({
        issue: issue.trim(),
        email: email.trim() || undefined,
        history_id: result.history_id || undefined,
        verdict: result.verdict,
        medication: medLabel,
        product: result.product_name,
      });
      setSent(true);
      toast.success("Thanks — we'll review this result.");
    } catch (e) {
      toast.error("Couldn't send that — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <p data-testid="report-result-sent" className="text-center text-[12px] text-ink-mute">
        Thanks for flagging this — a reviewer will take a look.
      </p>
    );
  }

  return (
    <div className="text-center">
      {!open ? (
        <button
          type="button"
          data-testid="report-result-toggle"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-ink-mute hover:text-ink transition-colors"
        >
          <Flag className="w-3.5 h-3.5" />
          Report this result
        </button>
      ) : (
        <div
          data-testid="report-result-form"
          className="mx-auto max-w-md rounded-2xl border border-surface-line bg-white p-5 text-left space-y-3"
        >
          <div className="text-[13px] font-bold text-ink">What looks wrong?</div>
          <textarea
            data-testid="report-result-issue"
            value={issue}
            onChange={(e) => setIssue(e.target.value)}
            rows={3}
            placeholder="Tell us what's inaccurate or confusing about this result…"
            className="w-full rounded-xl border border-surface-line bg-cream-50 px-3 py-2 text-[13px] text-ink focus:outline-none focus:ring-2 focus:ring-peach-100"
          />
          <input
            data-testid="report-result-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email (optional — if you'd like a reply)"
            className="w-full rounded-xl border border-surface-line bg-cream-50 px-3 py-2 text-[13px] text-ink focus:outline-none focus:ring-2 focus:ring-peach-100"
          />
          <div className="flex items-center gap-2">
            <Button
              data-testid="report-result-submit"
              onClick={submit}
              disabled={busy || !issue.trim()}
              className="rounded-full h-9 bg-ink text-cream-50 hover:bg-ink-soft text-[13px]"
            >
              {busy ? "Sending…" : "Send report"}
            </Button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[12px] text-ink-mute hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
